import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
  type QueryResponseData,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Customer } from "../../customer/index.js";
import { Project } from "../../project/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { Extension } from "../Extension/index.js";
import { ExtensionInstanceContext } from "./ExtensionInstanceContext.js";
import { ExtensionInstanceContract } from "./ExtensionInstanceContract";
import type {
  ExtensionInstanceConsentToScopesRequestData,
  ExtensionInstanceCreateRequestData,
  ExtensionInstanceData,
  ExtensionInstanceListItemData,
  ExtensionInstanceListQueryModelData,
} from "./types.js";

export class ExtensionInstance extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "extension",
    "extensionInstance",
  );

  public static ofId(id: string) {
    return new ExtensionInstance(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.extensionInstance.find(id);

    if (data) {
      return new ExtensionInstanceDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const extensionInstance = await this.find(id);
    assertObjectFound(extensionInstance, "extensionInstance", id);
    return extensionInstance;
  });

  public findDetailed = provideReact(
    () => ExtensionInstance.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<
    () => Promise<ExtensionInstanceDetailed | undefined>
  >;

  public getDetailed = provideReact(
    () => ExtensionInstance.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ExtensionInstanceDetailed>>;

  public static query(query: ExtensionInstanceListQueryModelData = {}) {
    return new ExtensionInstanceListQuery(query);
  }

  public static async create(data: ExtensionInstanceCreateRequestData) {
    const { id } = await config.behaviors.extensionInstance.create(data);

    return new ExtensionInstance(id);
  }

  public async consentToScopes(
    data: ExtensionInstanceConsentToScopesRequestData,
  ) {
    await config.behaviors.extensionInstance.consentToScopes(this.id, data);
  }

  public async enable() {
    await config.behaviors.extensionInstance.enable(this.id);
  }

  public async disable() {
    await config.behaviors.extensionInstance.disable(this.id);
  }

  public async delete() {
    await config.behaviors.extensionInstance.delete(this.id);
  }

  public async createRetrievalKey() {
    return await config.behaviors.extensionInstance.createRetrievalKey(this.id);
  }

  public findContract = provideReact(async () => {
    const data = await config.behaviors.extensionInstance.findContract(this.id);

    if (data) {
      return new ExtensionInstanceContract(data);
    }
  }, [this.id]);

  public async updateContract() {
    return await config.behaviors.extensionInstance.updateContract(this.id);
  }

  public async terminate(instantTermination: boolean) {
    await config.behaviors.extensionInstance.terminate(
      this.id,
      instantTermination,
    );
  }

  public async cancelTermination() {
    await config.behaviors.extensionInstance.cancelTermination(this.id);
  }
}

export class ExtensionInstanceCommon extends classes(
  DataModel<ExtensionInstanceData | ExtensionInstanceListItemData>,
  ExtensionInstance,
) {
  public readonly extension: Extension;
  public readonly pendingInstallation: boolean;
  public readonly consentedScopes: string[];
  public readonly context: ExtensionInstanceContext;

  public constructor(
    data: ExtensionInstanceData | ExtensionInstanceListItemData,
  ) {
    super([data], [data.id]);
    this.extension = Extension.ofId(data.extensionId);
    this.pendingInstallation = data.pendingInstallation;
    this.consentedScopes = data.consentedScopes;
    this.context = new ExtensionInstanceContext(
      data.aggregateReference.aggregate === Project.aggregateMetaData.aggregate
        ? Project.ofId(data.aggregateReference.id)
        : Customer.ofId(data.aggregateReference.id),
    );
  }

  public async getUrlWithParameters(url: string) {
    const retrievalKeyData = await this.createRetrievalKey();

    const extension = await this.extension.getDetailed();

    return url
      .replace(":userId", retrievalKeyData.userId)
      .replace(":contextId", this.context.value.id)
      .replace(":context", extension.context)
      .replace(":contributorId", extension.data.contributorId)
      .replace(":extensionId", this.data.extensionId)
      .replace(":extensionInstanceId", this.id)
      .replace(
        ":accessTokenRetrievalKey",
        retrievalKeyData.accessTokenRetrievalKey,
      )
      .replace(":apiVersion", "v1");
  }

  public async openUrlWithParameters(url: string) {
    const newWindow = window.open("about:blank", "_blank");

    const link = await this.getUrlWithParameters(url);

    if (link && newWindow) {
      newWindow.location.href = link;
    }
  }

  public scopesChanged = provideReact(async () => {
    const extension = await this.extension.getDetailed();

    if (extension.scopes.length !== this.consentedScopes.length) return true;

    return ![...extension.scopes]
      .sort()
      .every((val, index) => val === [...this.consentedScopes].sort()[index]);
  });
}

export class ExtensionInstanceDetailed extends classes(
  ExtensionInstanceCommon,
  DataModel<ExtensionInstanceData>,
) {
  public constructor(data: ExtensionInstanceData) {
    super([data], [data]);
  }
}

export class ExtensionInstanceListItem extends classes(
  ExtensionInstanceCommon,
  DataModel<ExtensionInstanceListItemData>,
) {
  public constructor(data: ExtensionInstanceListItemData) {
    super([data], [data]);
  }
}

export class ExtensionInstanceListQuery extends ListQueryModel<ExtensionInstanceListQueryModelData> {
  public constructor(query: ExtensionInstanceListQueryModelData) {
    super(query);
  }

  public refine(query: ExtensionInstanceListQueryModelData) {
    return new ExtensionInstanceListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { project, customer, ...restQuery } = this.query;

    type Result = QueryResponseData<ExtensionInstanceListItemData>;

    const emptyResult: Result = {
      items: [],
      totalCount: 0,
    };

    const projectResult = project
      ? await config.behaviors.extensionInstance.list({
          ...restQuery,
          context: "project",
          contextId: extractId(project),
        })
      : emptyResult;

    const projectMembership = project
      ? await Project.ofReference(project)?.getOwnMembership()
      : undefined;

    const hasAccessToCustomer = projectMembership?.inherited ?? true;

    const customerResult =
      customer && hasAccessToCustomer
        ? await config.behaviors.extensionInstance.list({
            ...restQuery,
            context: "customer",
            contextId: extractId(customer),
          })
        : emptyResult;

    const baseResult =
      !customer && !project
        ? await config.behaviors.extensionInstance.list({
            ...restQuery,
          })
        : emptyResult;

    const combinedResult: Result = {
      items: [
        ...projectResult.items,
        ...customerResult.items,
        ...baseResult.items,
      ],
      totalCount:
        projectResult.totalCount +
        customerResult.totalCount +
        baseResult.totalCount,
    };

    return new ExtensionInstanceList(
      this.query,
      combinedResult.items.map((d) => new ExtensionInstanceListItem(d)),
      //totalCount,
    );
  }, [this.queryId, "extensionInstance"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "extensionInstance"]);
}

export class ExtensionInstanceList extends classes(
  ExtensionInstanceListQuery,
  ListDataModel<ExtensionInstanceListItem>,
) {
  public constructor(
    query: ExtensionInstanceListQueryModelData,
    extensionInstances: ExtensionInstanceListItem[],
    // todo: warten bis gefixt
    //totalCount: number,
  ) {
    super([query], [extensionInstances, extensionInstances.length]);
  }
}
